import React, { useEffect, useRef } from "react";
import { cellDiff, columnState, hasAction, rowState, setCell, setColumn, setRow } from "../features/access/matrix";
import "./PermissionMatrix.css";

const UNUSED_HINT = "Действие пока не используется в этом разделе: отметить можно, но доступ оно не откроет.";

/** Чекбокс с тремя состояниями: all / some / none. */
function TriCheckbox({ state, disabled, onChange, label }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = state === "some";
  }, [state]);
  return (
    <input
      ref={ref}
      type="checkbox"
      className="pm-check"
      checked={state === "all"}
      disabled={disabled}
      aria-label={label}
      onChange={() => onChange(state !== "all")}
    />
  );
}

/**
 * Матрица «раздел × действие», как Role Permission Manager в ERPNext.
 *
 * - `registry` — ответ `GET /api/access/resources/`: `{actions, sections: [{resources}]}`;
 * - `value` — матрица `{раздел: [действия]}`;
 * - `onChange` — без него матрица только для чтения;
 * - `baseline` — с чем сравнивать: клетки, выданные сверх базы, подсвечиваются как «добавлено»,
 *   снятые — как «снято». Для роли база — сохранённая версия, для пользователя — права его ролей;
 * - `legend` — подписи к подсветке `{added, removed}`; без неё легенда не показывается.
 *
 * Включаема любая клетка. Действие, которое раздел пока не проверяет (`used_actions`),
 * приглушено и подписано — чтобы галочка там не создавала ложного ощущения доступа.
 */
export default function PermissionMatrix({ registry, value, onChange, baseline = null, legend = null }) {
  const readOnly = !onChange;
  const actions = registry.actions;
  const allResources = registry.sections.flatMap((s) => s.resources);
  const matrix = value || {};

  return (
    <div className="perm-matrix-wrap">
      {legend && baseline && (
        <div className="pm-legend">
          <span className="pm-legend-item">
            <span className="pm-swatch pm-swatch--added" /> {legend.added}
          </span>
          <span className="pm-legend-item">
            <span className="pm-swatch pm-swatch--removed" /> {legend.removed}
          </span>
          <span className="pm-legend-item">
            <span className="pm-swatch pm-swatch--unused" /> действие пока не используется разделом
          </span>
        </div>
      )}
      <div className={`perm-matrix${readOnly ? " perm-matrix--readonly" : ""}`}>
        <table className="pm-table">
          <thead>
            <tr>
              <th className="pm-th pm-th--name">Раздел</th>
              {actions.map((a) => (
                <th key={a.key} className="pm-th">
                  <label className="pm-col-head">
                    {!readOnly && (
                      <TriCheckbox
                        state={columnState(matrix, allResources, a.key)}
                        label={`${a.label}: все разделы`}
                        onChange={(on) => onChange(setColumn(matrix, allResources, a.key, on))}
                      />
                    )}
                    <span>{a.label}</span>
                  </label>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {registry.sections.map((section) => (
              <React.Fragment key={section.key}>
                <tr className="pm-section">
                  <td className="pm-td pm-td--name pm-section-name">{section.label}</td>
                  {actions.map((a) => (
                    <td key={a.key} className="pm-td pm-td--cell">
                      {!readOnly && (
                        <TriCheckbox
                          state={columnState(matrix, section.resources, a.key)}
                          label={`${section.label} — ${a.label}`}
                          onChange={(on) => onChange(setColumn(matrix, section.resources, a.key, on))}
                        />
                      )}
                    </td>
                  ))}
                </tr>
                {section.resources.map((resource) => (
                  <tr key={resource.key} className="pm-row">
                    <td className="pm-td pm-td--name">
                      {readOnly ? (
                        <span className="pm-name">{resource.label}</span>
                      ) : (
                        <button
                          type="button"
                          className="pm-name pm-name--button"
                          title="Отметить или снять все действия раздела"
                          onClick={() => onChange(setRow(matrix, resource, rowState(matrix, resource) !== "all"))}
                        >
                          {resource.label}
                        </button>
                      )}
                      {resource.description && <span className="pm-desc">{resource.description}</span>}
                    </td>
                    {actions.map((a) => {
                      const on = hasAction(matrix, resource.key, a.key);
                      const unused = !(resource.used_actions || []).includes(a.key);
                      const diff = cellDiff(matrix, baseline, resource.key, a.key);
                      const cls = [
                        "pm-td",
                        "pm-td--cell",
                        unused ? "pm-td--unused" : "",
                        diff ? `pm-td--${diff}` : "",
                      ]
                        .filter(Boolean)
                        .join(" ");
                      return (
                        <td key={a.key} className={cls} title={unused ? UNUSED_HINT : undefined}>
                          {readOnly ? (
                            <span className={on ? "pm-yes" : "pm-no"} aria-label={on ? "да" : "нет"}>
                              {on ? "✓" : "·"}
                            </span>
                          ) : (
                            <input
                              type="checkbox"
                              className="pm-check"
                              checked={on}
                              data-diff={diff || undefined}
                              data-unused={unused || undefined}
                              aria-label={`${resource.label} — ${a.label}`}
                              onChange={(e) => onChange(setCell(matrix, resource, a.key, e.target.checked))}
                            />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
